import { useCallback, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useAuth } from "react-oidc-context";
import DownloadIcon from "@mui/icons-material/Download";
import ReplayIcon from "@mui/icons-material/Replay";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { useTranslation } from "i18n-fe";
import { useScoreContext } from "../context/ScoreContext";
import { syncEngine } from "../local/sync";
import { dumpDatabase } from "../local/db";
import { localDataExportFilename, triggerJsonDownload } from "../util/localDataExport";
import { classNumberLabel, formatVenueTime } from "../util/classDisplay";
import SplitClassChip from "./SplitClassChip";
import {
  describeOutboxOps,
  describeRideForDiscard,
  isClaimable,
  isForeignClaimableForDisplay,
  isHeldForDisplay,
  isNotAssignedScribeError,
} from "../local/outboxDetail";

// #1921 — i18n keys must be static literals (never `t(\`prefix.${var}\`)`), so
// op type -> label is a plain switch of literal t() calls rather than a
// template-literal key built off op.type.
function opTypeLabel(t, type) {
  switch (type) {
    case "createRide":
      return t("outbox.opType.createRide");
    case "createScoreSheet":
      return t("outbox.opType.createScoreSheet");
    case "updateScoreSheet":
      return t("outbox.opType.updateScoreSheet");
    case "completeScoreSheet":
      return t("outbox.opType.completeScoreSheet");
    case "confirmScoreSheetMarks":
      return t("outbox.opType.confirmScoreSheetMarks");
    case "panelSignScoreSheet":
      return t("outbox.opType.panelSignScoreSheet");
    case "raiseUtilityEvent":
      return t("outbox.opType.raiseUtilityEvent");
    case "clearUtilityEvent":
      return t("outbox.opType.clearUtilityEvent");
    case "requestRideReview":
      return t("outbox.opType.requestRideReview");
    case "setRideSubsection":
      return t("outbox.opType.setRideSubsection");
    default:
      return type;
  }
}

// The outbox dialog (retry, claim, download local data). Card #3546: there is
// no Discard and no Send for review here: the tablet never gives up on an op
// and score-api holds anything it would refuse, so nothing in this dialog
// asks the scribe to quarantine or throw away a score. #3437 -- this used to be a family of sync chips
// (error/retrying, stuck, held, blocked, pending) in the session header with
// this dialog behind a manage button. A scribe cannot fix any of those states
// by looking at a chip (the admin sees the same counts on the Station Monitor,
// #3436 heartbeat health), so the chips are gone and the dialog is opened
// from the TopBar menu instead (TopBar.jsx). The counts (pendingSync,
// errorSync, ...) stay in ScoreContext for the heartbeat; nothing here
// renders them as an alarm.
//
// #1921 -- the dialog names every stuck op (ride, judge position, show,
// attempt count, errorClass, raw lastError), explains a known status in plain
// words, and lets the whole outbox be exported for a ringside diagnosis with
// no devtools.
//
// Controlled: `open` / `onClose`. The parent mounts it only while open.
export default function SyncStatus({ open = false, onClose }) {
  const { pendingSync, errorSync, terminalSync, heldSync, blockedSync, opsRevision } =
    useScoreContext();
  const { t } = useTranslation("score");
  const auth = useAuth();
  const [ops, setOps] = useState([]);
  // #1921 AC7 — size of the FULL outbox (pending + error + terminal), kept
  // separate from `ops` (which only ever holds SURFACED/stuck rows, the
  // AC4 list). Download must stay enabled off this count, not ops.length,
  // or a queue that hasn't surfaced anything yet reads as nothing-to-export.
  const [outboxCount, setOutboxCount] = useState(0);
  const [loading, setLoading] = useState(false);
  // #2111 — which op is mid-retry, so its row shows a spinner and can't be
  // double-tapped; null when nothing is retrying.
  const [retryingOpId, setRetryingOpId] = useState(null);
  // #2031 — which op is mid-claim, so its row shows a spinner and can't be
  // double-tapped; null when nothing is being claimed. Mirrors retryingOpId.
  const [claimingOpId, setClaimingOpId] = useState(null);
  // #2111 — a kiosk reaching for this download is often ALREADY in a bad
  // IndexedDB state (blocked/closing connection, the same class of failure
  // that can leave sync stuck in the first place), so dumpDatabase() failing
  // outright is a real case, not a hypothetical. Surfaced in words rather
  // than a silent no-op button.
  const [downloadError, setDownloadError] = useState(false);

  // Card 3622 -- "Loading…" replaces the whole list, so it shows only for the
  // FIRST load after opening. A refresh driven by engine progress (an attempt
  // finishing) swaps the rows in place: blanking them on every background
  // attempt made the Retry button vanish under the admin's finger.
  const loadedRef = useRef(false);
  // Refreshes overlap (one per finished attempt); only the newest request's
  // result may land, or a slower older read would put stale rows back.
  const refreshSeqRef = useRef(0);
  const refreshDetail = useCallback(async () => {
    const seq = (refreshSeqRef.current += 1);
    if (!loadedRef.current) setLoading(true);
    try {
      const [surfaced, all] = await Promise.all([
        syncEngine.listSurfacedOps(),
        syncEngine.listAllOps(),
      ]);
      const described = await describeOutboxOps(surfaced);
      if (seq !== refreshSeqRef.current) return;
      setOps(described);
      setOutboxCount(all.length);
      loadedRef.current = true;
    } finally {
      if (seq === refreshSeqRef.current) setLoading(false);
    }
  }, []);

  // Load the outbox rows when the dialog opens, and again whenever the engine's
  // counts move while it is open (a retry landing, the session sub arriving
  // and flipping rows to/from held), so the dialog never shows a stale list.
  useEffect(() => {
    if (open) void refreshDetail();
    else loadedRef.current = false;
    // Card 3622 -- opsRevision moves whenever an attempt finishes, even when no
    // count did (a 2nd failure is still one error), so attempts stay live.
  }, [open, refreshDetail, pendingSync, errorSync, terminalSync, heldSync, blockedSync, opsRevision]);

  const closeDetail = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // #2111 — replaces the old clipboard-only "Copy outbox" export (AC1/AC2):
  // a real file download of the WHOLE local database — every store in
  // db.js's STORES, not just the outbox — via dumpDatabase(). A scribe
  // diagnosing a stuck kiosk with no devtools hands over one file that
  // covers a bad/never-hydrated class or ride cache just as well as a bad
  // outbox row, not only the outbox. Filename per the card spec: fv_scribe_
  // <email snake cased>_<timestamp>.json. Read-only, dumped fresh at
  // download time so it never goes stale against ops enqueued after the
  // dialog opened.
  const downloadLocalData = useCallback(async () => {
    try {
      const dump = await dumpDatabase();
      const filename = localDataExportFilename(auth.user?.profile?.email);
      triggerJsonDownload(filename, dump);
      setDownloadError(false);
    } catch {
      // #2111 — dumpDatabase()/Blob/anchor can all reject/throw (a blocked
      // or closing IndexedDB connection, a browser that refuses the
      // download). Never swallow it: this button is the recovery affordance
      // itself, so a silent failure here is the worst possible place for one.
      setDownloadError(true);
    }
  }, [auth.user]);

  // #2111 AC4 — human-initiated retry of exactly one surfaced op, wired to
  // syncEngine.retryOp. Held rows never get here: the button itself is
  // gated on !isHeldForDisplay(op) below (a held op is blocked on
  // ownership, not a failure a retry can fix — see retryOp's own doc
  // comment in sync.js).
  const retryOp = useCallback(
    async (opId) => {
      setRetryingOpId(opId);
      try {
        await syncEngine.retryOp(opId);
        await refreshDetail();
      } finally {
        setRetryingOpId(null);
      }
    },
    [refreshDetail],
  );

  // #2031 AC1 — human-initiated claim of exactly one ownerless held op, wired
  // to syncEngine.claimOp. Offered only when isClaimable(op) gates the button
  // in OutboxOpRow below (AC2's hard boundary: never for an op owned by a
  // different, known sub).
  const claimOp = useCallback(
    async (opId) => {
      setClaimingOpId(opId);
      try {
        await syncEngine.claimOp(opId);
        await refreshDetail();
      } finally {
        setClaimingOpId(null);
      }
    },
    [refreshDetail],
  );

  // #1921: the detail dialog is reachable before anything has surfaced (that's
  // what makes the AC7 export reachable), and in that state the stuck-items wording is simply false: a scribe with three
  // queued writes was being told "Nothing is stuck right now." under a title
  // reading "Stuck sync items", and then handed a Copy outbox button that
  // exports all three. Say what is actually true instead: the queue is
  // non-empty, none of it has gone wrong, and keep "Nothing is stuck right
  // now." for the genuinely-empty outbox it was written for.
  const queuedNoneStuck = !loading && ops.length === 0 && outboxCount > 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={closeDetail}
        maxWidth="sm"
        fullWidth
        data-testid="sync-detail-dialog"
      >
        <DialogTitle>
          {queuedNoneStuck
            ? t("outbox.detailTitleQueued")
            : t("outbox.detailTitle")}
        </DialogTitle>
        <DialogContent>
          {/* #1923 AC4/AC3 — surfaced once, plainly, at the top of the same
              detail view #1921 built, rather than a separate place to look:
              "N queued items belong to a previous session and were not
              sent." Each held row below still gets its own explanation
              (OutboxOpRow), this banner is the one-line summary. */}
          {!loading && heldSync > 0 && (
            <Typography
              variant="body2"
              color="text.primary"
              data-testid="sync-detail-held-banner"
              sx={{ mb: 1 }}
            >
              {t("outbox.heldBanner", { count: heldSync })}
            </Typography>
          )}
          {/* #2031 AC3 — same one-line-summary pattern as the held banner
              above, but with accurate wording: a blocked op was NOT queued by
              someone else, it's withheld by its own createRide op still
              being stuck (#882's ordering gate), so "belongs to a previous
              session" would be a false claim about it. */}
          {!loading && blockedSync > 0 && (
            <Typography
              variant="body2"
              color="text.primary"
              data-testid="sync-detail-blocked-banner"
              sx={{ mb: 1 }}
            >
              {t("outbox.blockedBanner", { count: blockedSync })}
            </Typography>
          )}
          {loading ? (
            <Typography>{t("outbox.loading")}</Typography>
          ) : queuedNoneStuck ? (
            <Typography data-testid="sync-detail-queued">
              {t("outbox.queuedNoneStuck", { count: outboxCount })}
            </Typography>
          ) : ops.length === 0 ? (
            <Typography>{t("outbox.empty")}</Typography>
          ) : (
            <List data-testid="sync-detail-list" disablePadding>
              {ops.map((op) => (
                <OutboxOpRow
                  key={op.opId}
                  op={op}
                  t={t}
                  onRetry={() => retryOp(op.opId)}
                  retrying={retryingOpId === op.opId}
                  onClaim={() => claimOp(op.opId)}
                  claiming={claimingOpId === op.opId}
                />
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap" }}>
          {/* #2111 — downloadLocalData's catch branch above; visible rather
              than a silent no-op on a button that IS the recovery path. */}
          {downloadError && (
            <Typography
              variant="body2"
              color="error"
              data-testid="sync-detail-download-error"
              sx={{ width: "100%", mb: 0.5 }}
            >
              {t("outbox.downloadFailed")}
            </Typography>
          )}
          {/* #1921 (screenshot-caught, dark mode) — a plain text Button
              defaults to MUI's primary color, which is too close to the
              dark Paper background to read (same "primary grey invisible
              in dark" issue as elsewhere in this app). color="inherit"
              mirrors RideList.jsx's own dialog Cancel buttons. */}
          {/* #2111 — unlike the old Copy Outbox (gated on outboxCount so it
              couldn't export nothing), this now dumps the WHOLE local
              database, not just the outbox, and this dialog can only ever
              be open while pendingSync > 0 (see the early `!pendingSync`
              return above) — so there is always something to hand over. */}
          <Button
            color="inherit"
            startIcon={<DownloadIcon />}
            onClick={downloadLocalData}
            data-testid="sync-detail-download"
          >
            {t("outbox.download")}
          </Button>
          <Button
            color="inherit"
            onClick={closeDetail}
            data-testid="sync-detail-close"
          >
            {t("outbox.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// #1921 — one row in the detail dialog: everything the AC asks for (op
// type, ride, judge position, show, attempt count, errorClass, raw
// lastError), a plain-words explanation when the status is a known one
// (currently just #1562's 403).
// #2111 AC3/AC4/AC5 — plus the class + ride time (so a scribe can tell
// which ride on the sheet this is without leaving the dialog), a per-op
// Retry button (non-held rows only — see retryOp's doc comment in
// sync.js), and a closed-by-default error-detail expander.
// #2031 AC1/AC2/AC3 — plus a per-op Claim button (held-and-ownerless rows
// only, see isClaimable), and a distinct explanation/no-Retry treatment for
// a row that's BLOCKED (#882 ordering gate) rather than held (ownership).
function OutboxOpRow({
  op,
  t,
  onRetry,
  retrying,
  onClaim,
  claiming,
}) {
  const notAssignedScribe = isNotAssignedScribeError(op);
  // #1923 / #2031 — now reads real ownership (isHeldForDisplay is
  // owner-aware, see its own comment in outboxDetail.js) rather than
  // guessing from op.status.
  const held = isHeldForDisplay(op);
  // #2031 AC3 — a distinct cause from ownership: withheld by the #882
  // ordering gate because its own createRide op is itself stuck. Only
  // meaningful when the row isn't ALSO ownership-held — "held wins" is the
  // same one-op-one-state-one-wording rule sync.js's heldCount already
  // applies (#1923 F4).
  const blocked = !held && op.blockedOnStuckRide;
  // #2031 AC2 — the hard boundary: Claim is offered ONLY for a held row with
  // NO owner at all, never for one owned by a different, known sub.
  const claimable = isClaimable(op);
  // Card #3255 -- claimable because ANOTHER scribe's login queued it (same org),
  // not because it has no owner: different wording, same Claim action.
  const foreignClaim = isForeignClaimableForDisplay(op);
  const classLabel = classNumberLabel(op);
  const rideTime = formatVenueTime(op.rideScheduledTime, { zone: op.rideScheduledTimeZone });
  return (
    <>
      <ListItem
        data-testid="sync-detail-op"
        data-op-status={op.status}
        data-op-held={held}
        data-op-blocked={blocked}
        data-op-claimable={claimable}
        alignItems="flex-start"
        secondaryAction={
          <Stack direction="row" spacing={0.5}>
            {/* #2031 AC1/AC2 — offered only for a claimable (held, no
                owner) row; never for a row owned by a different, known sub
                (that row is still `held`, but isClaimable reads false for
                it), and never for a blocked row (claiming ownership does
                nothing for an ordering-gate block). */}
            {claimable && (
              <IconButton
                aria-label={
                  foreignClaim
                    ? t("outbox.claimForeignAria")
                    : t("outbox.claimAria")
                }
                data-testid="sync-claim-op"
                onClick={onClaim}
                disabled={claiming}
              >
                {claiming ? (
                  <CircularProgress size={20} />
                ) : (
                  <AssignmentIndIcon />
                )}
              </IconButton>
            )}
            {/* #2111 AC4 — judge bug finding: a held op is blocked on
                ownership, not a failure, so Retry never renders for one —
                sync.js's retryOp guards the same case server-side, this is
                the belt half of belt-and-braces. #2031 — a blocked row is
                the same story for a different reason (an ordering gate, not
                a failure): nothing retryOp does touches this op itself. */}
            {!held && !blocked && !foreignClaim && (
              <IconButton
                aria-label={t("outbox.retryAria")}
                data-testid="sync-retry-op"
                onClick={onRetry}
                disabled={retrying}
              >
                {retrying ? (
                  <CircularProgress size={20} />
                ) : (
                  <ReplayIcon />
                )}
              </IconButton>
            )}
          </Stack>
        }
      >
        <ListItemText
          secondaryTypographyProps={{ component: "div" }}
          primary={
            <Stack
              direction="row"
              spacing={1}
              alignItems="baseline"
              flexWrap="wrap"
            >
              <Typography variant="subtitle2" component="span">
                {opTypeLabel(t, op.type)}
              </Typography>
              {op.showName && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                >
                  {op.showName}
                </Typography>
              )}
            </Stack>
          }
          secondary={
            <Stack spacing={0.5} sx={{ mt: 0.5 }} component="span">
              <Typography variant="body2" component="span">
                {describeRideForDiscard(op)}
                {op.riderName ? ` — ${op.riderName}` : ""}
              </Typography>
              {/* #2111 AC3 — class + ride time, alongside show/entry/judge
                  position, shown regardless of held/error/terminal status
                  (same treatment as showName above): a scribe naming which
                  ride this is doesn't depend on whether it ever synced. */}
              {classLabel && (
                /* #1761 — outbox rows name their class exactly like every
                   other class surface, so a split section's row gets the
                   same Split/RO chip (off the class's staff-set
                   splitSection flag, #3564); two stuck items on a base class and its split
                   are otherwise identical "Class: 223. …" lines. */
                <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="span"
                    data-testid="sync-detail-op-class"
                  >
                    {t("outbox.classLabel", { classLabel })}
                  </Typography>
                  <SplitClassChip
                    klass={{ splitSection: op.splitSection }}
                    testId="sync-detail-op-split-chip"
                  />
                </Stack>
              )}
              {rideTime && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                  data-testid="sync-detail-op-time"
                >
                  {t("outbox.rideTime", { time: rideTime })}
                </Typography>
              )}
              {op.judgePosition && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                >
                  {t("outbox.judgePosition", { position: op.judgePosition })}
                </Typography>
              )}
              {/* #1923 / #2031 — a blocked op has never been attempted
                  (flush's #882 ordering gate skips it before it ever tries),
                  so its attempts/errorClass would read as "0 attempts" / an
                  empty error class: noise, not signal. A held op is NOT the
                  same guarantee — one attempted under its real owner before
                  that owner got swapped out (gap-3, isHeldForDisplay's own
                  comment) can carry real attempts/errorClass from that
                  earlier try. Either way the held/blocked explanations below
                  say the true, relevant thing instead, so both are hidden
                  here regardless. */}
              {!held && !blocked && (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="span"
                  >
                    {t("outbox.attempts", { count: op.attempts })}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="span"
                  >
                    {t("outbox.errorClass", { errorClass: op.errorClass })}
                  </Typography>
                </>
              )}
              {notAssignedScribe && (
                <Typography
                  variant="body2"
                  color="text.primary"
                  component="span"
                >
                  {t("outbox.explain403")}
                </Typography>
              )}
              {/* #2031 AC1/AC2 — two distinct wordings for two distinct
                  causes: a claimable (no-owner) row must invite the Claim
                  action, a foreign-known-owner row must NOT (it isn't
                  claimable, and offering the same "you can act on this"
                  framing would contradict the Claim button's own absence). */}
              {foreignClaim && (
                <Typography
                  variant="body2"
                  color="text.primary"
                  component="span"
                  data-testid="sync-detail-op-foreign-claim-explain"
                >
                  {t("outbox.explainForeignClaimable")}
                </Typography>
              )}
              {held && claimable && !foreignClaim && (
                <Typography
                  variant="body2"
                  color="text.primary"
                  component="span"
                  data-testid="sync-detail-op-held-explain"
                >
                  {t("outbox.explainHeldClaimable")}
                </Typography>
              )}
              {held && !claimable && (
                <Typography
                  variant="body2"
                  color="text.primary"
                  component="span"
                  data-testid="sync-detail-op-held-explain"
                >
                  {t("outbox.explainHeldForeign")}
                </Typography>
              )}
              {/* #2031 AC3 — a blocked row's own status stays "pending"
                  forever without this: it never displays as held (wrong
                  cause) and, before this card, never appeared in this list
                  at all. */}
              {blocked && (
                <Typography
                  variant="body2"
                  color="text.primary"
                  component="span"
                  data-testid="sync-detail-op-blocked-explain"
                >
                  {t("outbox.explainBlocked")}
                </Typography>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                component="span"
              >
                {op.lastError ?? t("outbox.noError")}
              </Typography>
              {/* #2111 AC5 — closed-by-default expander with the RAW API
                  detail. Held/blocked rows never attempted, so there's
                  nothing to show — same gate as attempts/errorClass above.
                  Defaults collapsed (no `expanded`/`defaultExpanded` prop). */}
              {!held && !blocked && (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    bgcolor: "transparent",
                    "&:before": { display: "none" },
                  }}
                  data-testid="sync-detail-op-error-expander"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon fontSize="small" />}
                    sx={{ px: 0, minHeight: 0, "& .MuiAccordionSummary-content": { my: 0.5 } }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {t("outbox.errorDetailToggle")}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 0 }}>
                    <Stack spacing={0.5}>
                      <Typography variant="caption">
                        {t("outbox.httpStatus", {
                          status: op.httpStatus ?? t("outbox.unknownValue"),
                        })}
                      </Typography>
                      <Typography variant="caption">
                        {t("outbox.errorClassDetail", {
                          errorClass: op.errorClass ?? t("outbox.unknownValue"),
                        })}
                      </Typography>
                      {/* #2726 — the two halves of "say why" the generic
                          message can't carry: score-api's machine-readable
                          failure code, and the field it actually refused
                          (marks_confirmed_frozen sends "movementScores" or
                          "collectives", #2725). Both come from the JSend fail
                          body via sync.js; installServerErrorInterceptor
                          discards them on its way to rawErrorDetail. Rendered
                          only when present, so a pre-#2726 row or a failure
                          with no JSend body (a network drop, a 5xx HTML page)
                          shows exactly what it always did. The value is
                          interpolated into a STATIC key — never a key built
                          from the server's string. */}
                      {op.failureCode && (
                        <Typography
                          variant="caption"
                          data-testid="sync-detail-op-failure-code"
                        >
                          {t("outbox.failureCode", { code: op.failureCode })}
                        </Typography>
                      )}
                      {op.failureField && (
                        <Typography
                          variant="caption"
                          data-testid="sync-detail-op-failure-field"
                        >
                          {t("outbox.failureField", { field: op.failureField })}
                        </Typography>
                      )}
                      {/* #3008 AC2 -- a same-field stale_write conflict stamps
                          `staleWriteConflictFields` on the op (sync.js).
                          Without this, the only way to see WHICH fields
                          conflicted was opening dev tools/IndexedDB. Rendered
                          only when the array is non-empty, into a STATIC key
                          interpolated with the (comma-joined) field names --
                          never a key built from server data. */}
                      {op.staleWriteConflictFields?.length > 0 && (
                        <Typography
                          variant="caption"
                          data-testid="sync-detail-op-conflict-fields"
                        >
                          {t("outbox.staleWriteConflictFields", {
                            fields: op.staleWriteConflictFields.join(", "),
                          })}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                        data-testid="sync-detail-op-raw-error"
                      >
                        {/* #2726 — when the interceptor stashed nothing (which,
                            measured, is every score-common client call in the
                            built bundle — see refused-save-keeps-text.cy.js's
                            CONFLICT_DETAIL comment) sync.js falls this back to
                            the body's own failure code, so this line can repeat
                            the "Failure code:" line above it. Deliberately NOT
                            de-duplicated: this line's whole job is to show what
                            was actually captured into rawErrorDetail, and
                            hiding it when it matches would make "captured the
                            code" and "captured nothing at all" look identical
                            in the one dialog a stuck scribe has for forensics.
                            #2111 — MUST fall back to lastError: after this
                            change lastError becomes the generic "Server
                            Error" for any API rejection, and a pre-#2111
                            row has rawErrorDetail: null, so without this
                            fallback the expander renders empty for it. */}
                        {op.rawErrorDetail ?? op.lastError ?? t("outbox.noError")}
                      </Typography>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}
            </Stack>
          }
        />
      </ListItem>
      <Divider component="li" />
    </>
  );
}
